import React, { useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  Heading,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Stat,
  StatLabel,
  StatNumber,
} from '@chakra-ui/react';
import { MdOutlineDescription, MdSearch, MdOpenInNew, MdPerson, MdGavel } from 'react-icons/md';
import AdvogadoDetailsModal from './AdvogadoDetailsModal';
import { isAdvogado } from '../../../../../../libs/advogado/advogadoUtils';

/**
 * Modal component for displaying PrecatoriosSelecionadasModal diario content
 */
const PrecatoriosSelecionadasModal = ({ 
  isOpen, 
  onClose, 
  selectedPrecatorio, 
  loadingDetails, 
  detailsError, 
  jurisconsultDetails,
  escavadorDetails, 
  handleOpenDetails 
}) => {
  // State for Advogado modal
  const [selectedAdvogado, setSelectedAdvogado] = useState(null);
  const [isAdvogadoModalOpen, setIsAdvogadoModalOpen] = useState(false);

  // Color mode values
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');
  const highlightColor = useColorModeValue('brand.primary', 'brand.secondary');
  const cardBg = useColorModeValue('white', 'gray.800');

  // Function to format date string from API
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  // Function to render key-value info fields
  const InfoField = ({ label, value }) => (
    <Box mb={2}>
      <Text fontSize="sm" color={textColor} fontWeight="medium">{label}</Text>
      <Text fontSize="md">{value || '-'}</Text>
    </Box>
  );

  // Function to safely render HTML content
  const renderHtmlContent = (content) => {
    if (!content) return null;
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  };

  // Function to get badge color based on status
  const getStatusColor = (status) => {
    const statusColors = {
      'ATIVO': 'green',
      'ARQUIVADO': 'red',
      'SUSPENSO': 'yellow',
      'BAIXADO': 'gray'
    };
    return statusColors[status] || 'blue';
  };

  // Handle advogado click
  const handleAdvogadoClick = (advogado) => {
    setSelectedAdvogado(advogado);
    setIsAdvogadoModalOpen(true);
  };

  // Handle advogado modal close
  const handleAdvogadoModalClose = () => {
    setIsAdvogadoModalOpen(false);
    setTimeout(() => {
      setSelectedAdvogado(null);
    }, 300);
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg={tableHeaderBg} borderTopRadius="md">
          {selectedPrecatorio ? (
            <Flex align="center" justify="space-between">
              <Text>Detalhes do Precatório</Text>
              <Badge colorScheme="blue" fontSize="sm">
                {selectedPrecatorio.numero_precatorio}
              </Badge>
            </Flex>
          ) : (
            'Carregando detalhes...'
          )}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody pt={4}>
          {loadingDetails ? (
            <VStack spacing={4} py={8}>
              <Spinner size="xl" color={highlightColor} thickness="4px" />
              <Text>Consultando detalhes...</Text>
              <Text fontSize="sm" color={textColor}>
                Esta operação pode levar algum tempo...
              </Text>
            </VStack>
          ) : detailsError ? (
            <Alert status="warning" variant="left-accent" mt={2}>
              <AlertIcon />
              <VStack align="start" spacing={2}>
                <Text fontWeight="bold">Não foi possível obter os detalhes</Text>
                <Text>{detailsError}</Text>
                <Text fontSize="sm">
                  Esta consulta pode estar indisponível temporariamente.
                </Text>
              </VStack>
            </Alert>
          ) : (jurisconsultDetails || escavadorDetails) ? (
            <Box>
              <Tabs colorScheme="blue" variant="enclosed" mt={2}>
                <TabList>
                  <Tab>Informações Básicas</Tab>
                  {jurisconsultDetails && <Tab>Conteúdo do Diário</Tab>}
                  {jurisconsultDetails?.movimentacoes && jurisconsultDetails.movimentacoes.length > 0 && (
                    <Tab>Movimentações</Tab>
                  )}
                  {escavadorDetails && <Tab>Processo Judicial</Tab>}
                  {escavadorDetails?.fontes && escavadorDetails.fontes.length > 0 && (
                    <Tab>Fontes do Processo</Tab>
                  )}
                </TabList>

                <TabPanels>
                  {/* Tab 1: Informações Básicas */}
                  <TabPanel>
                    <Card variant="outline" borderColor={borderColor} bg={cardBg} mb={4}>
                      <CardHeader pb={2}>
                        <Heading size="md">Dados do Precatório</Heading>
                      </CardHeader>
                      <Divider />
                      <CardBody>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                          <Box>
                            <InfoField 
                              label="Ente Devedor" 
                              value={selectedPrecatorio?.ente} 
                            />
                            <InfoField 
                              label="Natureza" 
                              value={selectedPrecatorio?.natureza} 
                            />
                            <InfoField 
                              label="Orçamento" 
                              value={selectedPrecatorio?.orcamento} 
                            />
                            <InfoField 
                              label="Recebimento" 
                              value={selectedPrecatorio?.recebimento} 
                            />
                            <InfoField 
                              label="Prioridade" 
                              value={selectedPrecatorio?.prioridade || 'Não'} 
                            />
                          </Box>
                          <Box>
                            {jurisconsultDetails && (
                              <>
                                <InfoField 
                                  label="Edição" 
                                  value={jurisconsultDetails?.edicao} 
                                />
                                <InfoField 
                                  label="Tipo" 
                                  value={jurisconsultDetails?.tipo} 
                                />
                                <InfoField 
                                  label="Data" 
                                  value={formatDate(jurisconsultDetails?.data)} 
                                />
                                <InfoField 
                                  label="Data de Publicação" 
                                  value={formatDate(jurisconsultDetails?.data_publicacao)} 
                                />
                                <InfoField 
                                  label="Origem" 
                                  value={jurisconsultDetails?.origem?.nome} 
                                />
                              </>
                            )}
                          </Box>
                        </SimpleGrid>
                      </CardBody>
                    </Card>

                    {/* Process Info */}
                    {escavadorDetails && (
                      <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                        <CardHeader pb={2}>
                          <Heading size="md">Informações do Processo</Heading>
                        </CardHeader>
                        <Divider />
                        <CardBody>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                            <Box>
                              <InfoField 
                                label="Número CNJ" 
                                value={escavadorDetails.numero_cnj} 
                              />
                              <InfoField 
                                label="Polo Ativo" 
                                value={escavadorDetails.titulo_polo_ativo} 
                              />
                              <InfoField 
                                label="Polo Passivo" 
                                value={escavadorDetails.titulo_polo_passivo} 
                              />
                              <InfoField 
                                label="Ano de Início" 
                                value={escavadorDetails.ano_inicio} 
                              />
                              <InfoField 
                                label="Data de Início" 
                                value={formatDate(escavadorDetails.data_inicio)} 
                              />
                            </Box>
                            <Box>
                              <InfoField 
                                label="Estado de Origem" 
                                value={`${escavadorDetails.estado_origem?.nome} (${escavadorDetails.estado_origem?.sigla})`} 
                              />
                              <InfoField 
                                label="Unidade de Origem" 
                                value={escavadorDetails.unidade_origem?.nome} 
                              />
                              <InfoField 
                                label="Última Movimentação" 
                                value={formatDate(escavadorDetails.data_ultima_movimentacao)} 
                              />
                              <InfoField 
                                label="Total de Movimentações" 
                                value={escavadorDetails.quantidade_movimentacoes} 
                              />
                              <InfoField 
                                label="Última Verificação" 
                                value={`${formatDate(escavadorDetails.data_ultima_verificacao)} (${escavadorDetails.tempo_desde_ultima_verificacao})`} 
                              />
                            </Box>
                          </SimpleGrid>
                        </CardBody>
                      </Card>
                    )}
                  </TabPanel>

                  {/* Tab 2: Conteúdo do Diário */}
                  {jurisconsultDetails && (
                    <TabPanel>
                      <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                        <CardHeader pb={2}>
                          <Heading size="md">Conteúdo do Diário</Heading>
                        </CardHeader>
                        <Divider />
                        <CardBody>
                          {jurisconsultDetails?.paginas?.items?.conteudo ? (
                            <Box 
                              p={4} 
                              border="1px" 
                              borderColor={borderColor} 
                              borderRadius="md" 
                              maxHeight="600px" 
                              overflowY="auto"
                            >
                              {renderHtmlContent(jurisconsultDetails.paginas.items.conteudo)}
                            </Box>
                          ) : (
                            <Alert status="info">
                              <AlertIcon />
                              Nenhum conteúdo de diário disponível para este precatório.
                            </Alert>
                          )}
                        </CardBody>
                      </Card>
                    </TabPanel>
                  )}

                  {/* Tab 3: Movimentações */}
                  {jurisconsultDetails?.movimentacoes && jurisconsultDetails.movimentacoes.length > 0 && (
                    <TabPanel>
                      <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                        <CardHeader pb={2}>
                          <Heading size="md">Movimentações do Processo</Heading>
                        </CardHeader>
                        <Divider />
                        <CardBody>
                          <VStack spacing={4} align="stretch">
                            {jurisconsultDetails.movimentacoes.map((mov, index) => (
                              <Card key={mov.id || index} variant="outline" size="sm">
                                <CardHeader pb={2}>
                                  <Flex justify="space-between" align="center">
                                    <Badge colorScheme="blue">{formatDate(mov.data)}</Badge>
                                    <Text fontSize="sm" color={textColor}>{mov.tipo || 'Movimentação'}</Text>
                                  </Flex>
                                </CardHeader>
                                <CardBody pt={2}>
                                  <Box 
                                    p={3} 
                                    bg={tableHeaderBg} 
                                    borderRadius="md"
                                    fontSize="sm"
                                  >
                                    {renderHtmlContent(mov.conteudo) || mov.texto_categoria || 'Sem detalhes disponíveis'}
                                  </Box>
                                </CardBody>
                              </Card>
                            ))}
                          </VStack>
                        </CardBody>
                      </Card>
                    </TabPanel>
                  )}

                  {/* Tab 4: Processo Judicial (Escavador) */}
                  {escavadorDetails && (
                    <TabPanel>
                      <VStack spacing={4} align="stretch">
                        {/* Basic Process Info Card */}
                        <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                          <CardHeader pb={2}>
                            <Heading size="md">Dados do Processo</Heading>
                          </CardHeader>
                          <Divider />
                          <CardBody>
                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                              <Box>
                                <HStack spacing={2} mb={2}>
                                  <Icon as={MdGavel} color={highlightColor} />
                                  <Text fontWeight="bold">Número CNJ</Text>
                                </HStack>
                                <Text>{escavadorDetails.numero_cnj}</Text>
                              </Box>
                              <Box>
                                <HStack spacing={2} mb={2}>
                                  <Icon as={MdPerson} color={highlightColor} />
                                  <Text fontWeight="bold">Polo Ativo</Text>
                                </HStack>
                                <Text>{escavadorDetails.titulo_polo_ativo || '-'}</Text>
                              </Box>
                              <Box>
                                <HStack spacing={2} mb={2}>
                                  <Icon as={MdPerson} color={textColor} />
                                  <Text fontWeight="bold">Polo Passivo</Text>
                                </HStack>
                                <Text>{escavadorDetails.titulo_polo_passivo || '-'}</Text>
                              </Box>
                            </SimpleGrid>
                          </CardBody>
                        </Card>

                        {/* Status Summary */}
                        <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                          <CardHeader pb={2}>
                            <Heading size="md">Resumo do Andamento</Heading>
                          </CardHeader>
                          <Divider />
                          <CardBody>
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                              <Stat>
                                <StatLabel>Data de Início</StatLabel>
                                <StatNumber fontSize="md">{formatDate(escavadorDetails.data_inicio)}</StatNumber>
                              </Stat>
                              <Stat>
                                <StatLabel>Última Movimentação</StatLabel>
                                <StatNumber fontSize="md">{formatDate(escavadorDetails.data_ultima_movimentacao)}</StatNumber>
                              </Stat>
                              <Stat>
                                <StatLabel>Total de Movimentações</StatLabel>
                                <StatNumber fontSize="md">{escavadorDetails.quantidade_movimentacoes}</StatNumber>
                              </Stat>
                              <Stat>
                                <StatLabel>Fontes Disponíveis</StatLabel>
                                <StatNumber fontSize="md">{escavadorDetails.fontes?.length || 0}</StatNumber>
                              </Stat>
                            </SimpleGrid>
                          </CardBody>
                        </Card>

                        {/* Related Processes */}
                        {escavadorDetails.processos_relacionados && escavadorDetails.processos_relacionados.length > 0 && (
                          <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                            <CardHeader pb={2}>
                              <Heading size="md">Processos Relacionados</Heading>
                            </CardHeader>
                            <Divider />
                            <CardBody>
                              <VStack spacing={2} align="stretch">
                                {escavadorDetails.processos_relacionados.map((proc, index) => (
                                  <Box key={index} p={2} border="1px" borderColor={borderColor} borderRadius="md">
                                    <Text fontSize="sm">{proc.numero || proc}</Text>
                                  </Box>
                                ))}
                              </VStack>
                            </CardBody>
                          </Card>
                        )}
                      </VStack>
                    </TabPanel>
                  )}

                  {/* Tab 5: Fontes do Processo */}
                  {escavadorDetails?.fontes && escavadorDetails.fontes.length > 0 && (
                    <TabPanel>
                      <Card variant="outline" borderColor={borderColor} bg={cardBg}>
                        <CardHeader pb={2}>
                          <Heading size="md">Fontes Disponíveis ({escavadorDetails.fontes.length})</Heading>
                        </CardHeader>
                        <Divider />
                        <CardBody>
                          <Accordion allowMultiple>
                            {escavadorDetails.fontes.map((fonte, index) => (
                              <AccordionItem key={fonte.processo_fonte_id || index}>
                                <h2>
                                  <AccordionButton>
                                    <Box flex="1" textAlign="left">
                                      <HStack spacing={4}>
                                        <Badge colorScheme={getStatusColor(fonte.status_predito)}>
                                          {fonte.status_predito || 'INDEFINIDO'}
                                        </Badge>
                                        <Text fontWeight="bold">{fonte.nome}</Text>
                                        <Text fontSize="sm" color={textColor}>
                                          {fonte.grau_formatado} - {fonte.descricao}
                                        </Text>
                                      </HStack>
                                    </Box>
                                    <AccordionIcon />
                                  </AccordionButton>
                                </h2>
                                <AccordionPanel pb={4}>
                                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <Box>
                                      <InfoField label="Sistema" value={fonte.sistema} />
                                      <InfoField label="Data de Início" value={formatDate(fonte.data_inicio)} />
                                      <InfoField label="Última Movimentação" value={formatDate(fonte.data_ultima_movimentacao)} />
                                      <InfoField label="Segredo de Justiça" value={fonte.segredo_justica ? 'Sim' : 'Não'} />
                                    </Box>
                                    <Box>
                                      <InfoField label="Quantidade de Envolvidos" value={fonte.quantidade_envolvidos} />
                                      <InfoField label="Quantidade de Movimentações" value={fonte.quantidade_movimentacoes} />
                                      <InfoField label="Última Verificação" value={formatDate(fonte.data_ultima_verificacao)} />
                                      <InfoField label="Processo Físico" value={fonte.fisico === null ? 'Não informado' : fonte.fisico ? 'Sim' : 'Não'} />
                                    </Box>
                                  </SimpleGrid>

                                  {/* Capa do Processo */}
                                  {fonte.capa && Object.keys(fonte.capa).length > 0 && (
                                    <Box mt={4}>
                                      <Text fontWeight="bold" mb={3}>Informações da Capa:</Text>
                                      <Card variant="outline" size="sm">
                                        <CardBody>
                                          <VStack spacing={3} align="stretch">
                                            {/* Main Information */}
                                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                              {fonte.capa.classe && (
                                                <Box>
                                                  <Text fontSize="xs" color={textColor} fontWeight="medium">Classe</Text>
                                                  <Text fontSize="sm">{fonte.capa.classe}</Text>
                                                </Box>
                                              )}
                                              {fonte.capa.assunto && (
                                                <Box>
                                                  <Text fontSize="xs" color={textColor} fontWeight="medium">Assunto</Text>
                                                  <Text fontSize="sm">{fonte.capa.assunto}</Text>
                                                </Box>
                                              )}
                                              {fonte.capa.area && (
                                                <Box>
                                                  <Text fontSize="xs" color={textColor} fontWeight="medium">Área</Text>
                                                  <Text fontSize="sm">{fonte.capa.area}</Text>
                                                </Box>
                                              )}
                                              {fonte.capa.orgao_julgador && (
                                                <Box>
                                                  <Text fontSize="xs" color={textColor} fontWeight="medium">Órgão Julgador</Text>
                                                  <Text fontSize="sm">{fonte.capa.orgao_julgador}</Text>
                                                </Box>
                                              )}
                                              {fonte.capa.situacao && (
                                                <Box>
                                                  <Text fontSize="xs" color={textColor} fontWeight="medium">Situação</Text>
                                                  <Badge colorScheme="blue" fontSize="xs">{fonte.capa.situacao}</Badge>
                                                </Box>
                                              )}
                                              {fonte.capa.data_distribuicao && (
                                                <Box>
                                                  <Text fontSize="xs" color={textColor} fontWeight="medium">Data de Distribuição</Text>
                                                  <Text fontSize="sm">{formatDate(fonte.capa.data_distribuicao)}</Text>
                                                </Box>
                                              )}
                                              {fonte.capa.data_arquivamento && (
                                                <Box>
                                                  <Text fontSize="xs" color={textColor} fontWeight="medium">Data de Arquivamento</Text>
                                                  <Text fontSize="sm">{formatDate(fonte.capa.data_arquivamento)}</Text>
                                                </Box>
                                              )}
                                            </SimpleGrid>

                                            {/* Valor da Causa */}
                                            {fonte.capa.valor_causa && (
                                              <Box p={3} bg={tableHeaderBg} borderRadius="md">
                                                <Text fontSize="xs" color={textColor} fontWeight="medium" mb={1}>
                                                  Valor da Causa
                                                </Text>
                                                <Text fontSize="lg" fontWeight="bold" color={highlightColor}>
                                                  {fonte.capa.valor_causa.valor_formatado || `${fonte.capa.valor_causa.moeda} ${fonte.capa.valor_causa.valor}`}
                                                </Text>
                                              </Box>
                                            )}

                                            {/* Informações Complementares */}
                                            {fonte.capa.informacoes_complementares && fonte.capa.informacoes_complementares.length > 0 && (
                                              <Box>
                                                <Divider my={2} />
                                                <Text fontSize="sm" fontWeight="medium" mb={2}>Informações Complementares:</Text>
                                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                                  {fonte.capa.informacoes_complementares.map((info, idx) => (
                                                    <HStack key={idx} spacing={2} align="start">
                                                      <Text fontSize="xs" color={textColor} minW="fit-content">
                                                        {info.tipo}:
                                                      </Text>
                                                      <Text fontSize="xs" fontWeight="medium">
                                                        {info.valor === "Não informado" ? (
                                                          <Badge variant="subtle" colorScheme="gray">Não informado</Badge>
                                                        ) : info.valor === "Não" ? (
                                                          <Badge variant="subtle" colorScheme="red">Não</Badge>
                                                        ) : info.valor === "Sim" ? (
                                                          <Badge variant="subtle" colorScheme="green">Sim</Badge>
                                                        ) : (
                                                          info.valor
                                                        )}
                                                      </Text>
                                                    </HStack>
                                                  ))}
                                                </SimpleGrid>
                                              </Box>
                                            )}

                                            {/* Assuntos Normalizados */}
                                            {fonte.capa.assuntos_normalizados && fonte.capa.assuntos_normalizados.length > 0 && (
                                              <Box>
                                                <Text fontSize="xs" color={textColor} fontWeight="medium" mb={1}>
                                                  Assuntos:
                                                </Text>
                                                <HStack spacing={2} flexWrap="wrap">
                                                  {fonte.capa.assuntos_normalizados.map((assunto, idx) => (
                                                    <Badge key={idx} variant="outline" colorScheme="purple" fontSize="xs">
                                                      {assunto}
                                                    </Badge>
                                                  ))}
                                                </HStack>
                                              </Box>
                                            )}
                                          </VStack>
                                        </CardBody>
                                      </Card>
                                    </Box>
                                  )}

                                  {/* Envolvidos */}
                                  {fonte.envolvidos && fonte.envolvidos.length > 0 && (
                                    <Box mt={4}>
                                      <Text fontWeight="bold" mb={2}>Partes Envolvidas ({fonte.envolvidos.length}):</Text>
                                      <VStack spacing={3} align="stretch">
                                        {fonte.envolvidos.map((env, idx) => (
                                          <Card key={idx} size="sm" variant="outline">
                                            <CardBody>
                                              <Flex justify="space-between" align="start" mb={2}>
                                                <Box flex="1">
                                                  <HStack spacing={2} mb={1}>
                                                    {isAdvogado(env) ? (
                                                      <Link
                                                        onClick={() => handleAdvogadoClick(env)}
                                                        color={highlightColor}
                                                        textDecoration="underline"
                                                        cursor="pointer"
                                                        _hover={{ color: 'blue.500' }}
                                                      >
                                                        <Text fontWeight="bold" fontSize="md">{env.nome}</Text>
                                                      </Link>
                                                    ) : (
                                                      <Text fontWeight="bold" fontSize="md">{env.nome}</Text>
                                                    )}
                                                    <Badge 
                                                      colorScheme={env.polo === 'ATIVO' ? 'green' : env.polo === 'PASSIVO' ? 'red' : 'blue'}
                                                      fontSize="xs"
                                                    >
                                                      {env.polo}
                                                    </Badge>
                                                    <Badge 
                                                      colorScheme={env.tipo_pessoa === 'JURIDICA' ? 'purple' : 'orange'}
                                                      fontSize="xs"
                                                    >
                                                      {env.tipo_pessoa}
                                                    </Badge>
                                                  </HStack>
                                                  <Text fontSize="sm" color={textColor}>
                                                    {env.tipo} {env.tipo !== env.tipo_normalizado && `(${env.tipo_normalizado})`}
                                                  </Text>
                                                </Box>
                                                <Text fontSize="xs" color={textColor}>
                                                  {env.quantidade_processos} processos
                                                </Text>
                                              </Flex>
                                              
                                              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} fontSize="sm">
                                                {/* CPF/CNPJ */}
                                                {(env.cpf || env.cnpj) && (
                                                  <Box>
                                                    <Text color={textColor} fontSize="xs">Documento:</Text>
                                                    <Text fontFamily="mono">
                                                      {env.cpf ? `CPF: ${env.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}` : ''}
                                                      {env.cnpj ? `CNPJ: ${env.cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}` : ''}
                                                    </Text>
                                                  </Box>
                                                )}
                                                
                                                {/* OAB */}
                                                {env.oabs && env.oabs.length > 0 && (
                                                  <Box>
                                                    <Text color={textColor} fontSize="xs">OAB:</Text>
                                                    <HStack spacing={2} flexWrap="wrap">
                                                      {env.oabs.map((oab, oabIdx) => (
                                                        <Badge key={oabIdx} variant="outline" colorScheme="teal">
                                                          {oab.uf} {oab.numero} ({oab.tipo})
                                                        </Badge>
                                                      ))}
                                                    </HStack>
                                                  </Box>
                                                )}
                                              </SimpleGrid>
                                              
                                              {/* Advogados relacionados */}
                                              {env.advogados && env.advogados.length > 0 && (
                                                <Box mt={2}>
                                                  <Text color={textColor} fontSize="xs" mb={1}>Advogados:</Text>
                                                  <VStack spacing={1} align="start">
                                                    {env.advogados.map((adv, advIdx) => (
                                                      <Text key={advIdx} fontSize="xs">
                                                        • {adv.nome || adv}
                                                      </Text>
                                                    ))}
                                                  </VStack>
                                                </Box>
                                              )}
                                            </CardBody>
                                          </Card>
                                        ))}
                                      </VStack>
                                    </Box>
                                  )}
                                </AccordionPanel>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </CardBody>
                      </Card>
                    </TabPanel>
                  )}
                </TabPanels>
              </Tabs>
            </Box>
          ) : selectedPrecatorio ? (
            <Alert status="info" variant="left-accent">
              <AlertIcon />
              <VStack align="start" spacing={2}>
                <Text>Informações do precatório {selectedPrecatorio.numero_precatorio}</Text>
                <Text fontSize="sm">
                  Clique em "Consultar" para buscar detalhes no sistema.
                </Text>
              </VStack>
            </Alert>
          ) : (
            <Text>Nenhum precatório selecionado</Text>
          )}
        </ModalBody>

        <ModalFooter>
          {!loadingDetails && selectedPrecatorio && !(jurisconsultDetails || escavadorDetails) && (
            <Button 
              colorScheme="blue" 
              leftIcon={<Icon as={MdSearch} />}
              mr={3}
              onClick={() => handleOpenDetails(selectedPrecatorio)}
              isDisabled={loadingDetails}
            >
              Consultar
            </Button>
          )}
          <Button onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    
    {/* Advogado Details Modal */}
    <AdvogadoDetailsModal
      isOpen={isAdvogadoModalOpen}
      onClose={handleAdvogadoModalClose}
      advogado={selectedAdvogado}
      estado="AM"
    />
    </>
  );
};

export default PrecatoriosSelecionadasModal;